<template>
  <div>
    <!-- HERO -->
    <section class="hero">
      <div class="hero__overlay"></div>
      <header class="header">
        <AppLogo asLink to="/" size="md" :showText="true" />
      </header>
    </section>

    <!-- 메인 카드 -->
    <main class="main">
      <div class="container">
        <div class="card signin-card">
          <div class="signin-inner">
            <div class="signin-head">
              <div class="signin-badge">로그인</div>
              <h1 class="signin-title">Sign in</h1>
            </div>

            <form class="signin-form" @submit.prevent="onLogin">
              <!-- 전화번호 -->
              <UiInput
                placeholder="전화번호 ( - 제외)"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                v-model="form.phone"
              />

              <!-- 비밀번호 -->
              <UiInput
                type="password"
                placeholder="비밀번호"
                v-model="form.password"
                autocomplete="current-password"
              />

              <UiButton
                type="submit"
                :disabled="!canSubmit || loading"
                :loading="loading"
              >
                로그인
              </UiButton>
            </form>

            <!-- 하단 링크 -->
            <div class="signin-links">
              <router-link to="/free-trial" class="signin-link">
                <span class="signin-link-text">무료체험 신청</span>
              </router-link>

              <router-link to="/forgot-password" class="signin-link">
                <span class="signin-link-text">비밀번호 재설정</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 푸터 -->
    <footer class="footer" style="margin-top: var(--space-64)">
      <div class="footer-bar">
        <span class="hint">Copyright ⓒ 2025 Cardio Factory</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { api } from "@/lib/api";
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AppLogo from "@/components/AppLogo.vue";
import { getMeOnce } from "@/lib/session";
import { safeRedirectPath, signedInLandingPath } from "@/lib/adminAccess";

export default {
  name: "SignInView",
  components: { UiButton, UiInput, AppLogo },
  data() {
    return {
      loading: false,
      form: { phone: "", password: "" },
    };
  },
  computed: {
    phoneValid() {
      const digits = (this.form.phone || "").replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 11;
    },
    canSubmit() {
      const { phone, password } = this.form;
      return !!password && this.phoneValid && !!phone;
    },
  },
  methods: {
    async onLogin() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;
      try {
        const res = await api("/v1/auth/sessions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number: this.form.phone.replace(/\D/g, ""),
            password: this.form.password,
          }),
        });
        if (!res.ok) throw new Error("로그인 실패");
        // 역할을 확인한 뒤 첫 화면을 고른다. 코치는 관리자 기본 화면을 잠깐
        // 거치지 않고 체험 고객 관리로 바로 들어간다.
        const me = await getMeOnce({ force: true });
        const redirect = safeRedirectPath(this.$route.query.redirect);
        this.$router.replace(redirect || signedInLandingPath(me?.role));
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 카드 */
.signin-card {
  max-width: 758px;
  margin: 0 auto;
  padding: 48px 56px;
  background: #151718;
  border-radius: 30px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
}

/* 내부 360px 컨테이너 */
.signin-inner {
  max-width: 360px;
  margin: 0 auto;
}

/* 제목 */
.signin-head {
  margin-bottom: 24px;
  text-align: left;
}
.signin-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}
.signin-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.signin-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* UiInput */
.signin-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}
.signin-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}
.signin-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

/* 버튼 */
.signin-form .ui-btn {
  width: 100%;
}

/* 하단 링크 */
.signin-links {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-top: 32px;
}

.signin-link {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s;
}

.signin-link:hover {
  opacity: 0.8;
}

.signin-link-text {
  color: #fff;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: nowrap;
}

.hero {
  height: 220px; /* 기존 hero 높이가 크면 줄이기 */
  position: relative;
}

/* 풀-블리드 푸터 바 */
.footer-bar {
  width: 100%;
  margin: 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  background: #151718;
}
</style>
